import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, ShieldCheck, Smartphone, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BrandMark, Field, ThemeToggle } from "@/components/common";

const Feature = ({ icon: Icon, title, text }) => (
  <div className="flex gap-4">
    <span className="h-11 w-11 rounded-xl bg-white/10 grid place-items-center shrink-0"><Icon className="h-5 w-5" /></span>
    <div><p className="font-semibold">{title}</p><p className="text-sm text-white/70">{text}</p></div>
  </div>
);

export default function LoginPage() {
  const { user, login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Selamat datang, ${u.name}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-background">
      <section className="hidden lg:flex relative overflow-hidden bg-[hsl(222_47%_9%)] text-white p-14 flex-col justify-between">
        <div className="absolute inset-0 login-pattern opacity-60" />
        <div className="absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-primary/40 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <BrandMark size="h-12 w-12" />
          <p className="font-heading text-xl font-bold">{settings.app_name}</p>
        </div>
        <div className="relative max-w-lg">
          <p className="eyebrow !text-white/60 mb-4">Sistem Koperasi Terpadu</p>
          <h1 className="font-heading text-5xl font-extrabold leading-[1.05] tracking-tight">Simpan, pinjam, dan tagih — <span className="text-primary">dari mana saja.</span></h1>
          <div className="mt-12 space-y-6">
            <Feature icon={Smartphone} title="Siap di lapangan" text="Petugas mencatat setoran & angsuran langsung dari ponsel." />
            <Feature icon={ShieldCheck} title="Akses sesuai peran" text="Wilayah, cabang, kasir, hingga petugas melihat yang perlu saja." />
            <Feature icon={TrendingUp} title="Ringkasan real-time" text="Total simpanan, pinjaman berjalan, dan transaksi harian." />
          </div>
        </div>
        <p className="relative text-xs text-white/50">© {new Date().getFullYear()} {settings.app_name}</p>
      </section>

      <section className="flex flex-col p-6 sm:p-10">
        <div className="flex justify-between items-center">
          <div className="lg:hidden flex items-center gap-2.5"><BrandMark size="h-10 w-10" /><p className="font-heading font-bold">{settings.app_name}</p></div>
          <ThemeToggle className="ml-auto" />
        </div>
        <div className="flex-1 grid place-items-center">
          <form onSubmit={submit} className="w-full max-w-sm space-y-6 animate-rise" data-testid="login-form">
            <div>
              <h2 className="font-heading text-3xl font-extrabold tracking-tight">Masuk</h2>
              <p className="text-sm text-muted-foreground mt-2">Gunakan email dan password akun Anda. Anda akan diarahkan ke dashboard sesuai peran.</p>
            </div>
            <Field label="Email">
              <Input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" placeholder="nama@koperasi.id" data-testid="login-email-input" />
            </Field>
            <Field label="Password">
              <div className="relative">
                <Input type={show ? "text" : "password"} required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 pr-12" data-testid="login-password-input" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center text-muted-foreground" aria-label="Tampilkan password" data-testid="login-toggle-password">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-full text-base group" data-testid="login-submit-button">
              {busy ? "Memproses..." : "Masuk"}<ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <p className="text-xs text-muted-foreground text-center">Akun dibuat oleh admin di atas Anda. Lupa password? Hubungi admin kantor Anda.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
